// Ported from Octo.Mobile@79a7c69 src/stores/metadataStore.ts
import { create } from 'zustand';
import { buildMenuTree, type SystemScreenUserRaw } from '../lib/buildMenuTree';
import type {
  SystemScreen,
  SystemObject,
  SystemObjectField,
  SystemObjectDetail,
  SystemObjectFilter,
  SystemHtmlCardTemplate,
  SystemHtmlSummaryTemplate,
  SystemRazorTemplate,
  UserScreenNode,
} from '@/shared/types/system';

/** Damga kontrolleri arasında en az bu kadar beklenir (ekran açılışları arka arkaya sorgu atmasın). */
/** Uçuştaki ikon isteği — eşzamanlı çağrılar aynı isteği paylaşır. */
let iconsInFlight: Promise<void> | null = null;

const STAMP_CHECK_MIN_INTERVAL_MS = 30_000;
import { systemApi } from '@/shared/api/systemApi';

// Raw API response type for UserScreens endpoint

interface MetadataState {
  screens: SystemScreen[];
  menuTree: UserScreenNode[];
  objects: Map<string, SystemObject>;
  fields: Map<string, SystemObjectField[]>;
  objectDetails: Map<string, SystemObjectDetail[]>;
  objectFilters: Map<string, SystemObjectFilter[]>;
  htmlCardTemplates: Map<string, SystemHtmlCardTemplate>;
  htmlSummaryTemplates: Map<string, SystemHtmlSummaryTemplate>;
  razorTemplates: Map<string, SystemRazorTemplate>;
  /** iconId → PNG base64 (GET /api/system/Icons) */
  icons: Map<string, string>;
  /** iconId → kütüphane kodu; ikon seçicide aramak için (aynı istekten geliyor) */
  iconCodes: Map<string, string>;
  iconsLoaded: boolean;
  /** Eklenen ikonu yerel haritaya işler — 3,4 MB'lık liste yeniden çekilmez. */
  upsertIcon: (iconId: string, iconCode: string, png: string) => void;
  /** Silinen ikonu yerel haritadan düşürür. */
  removeIcon: (iconId: string) => void;
  /** SummaryTypeId → "Sum" | "Count" | ... */
  summaryTypes: Map<string, string>;
  loadSummaryTypes: () => Promise<void>;
  /** Son bilinen metadata damgası; null ise henüz alınmadı */
  metadataStamp: string | null;
  lastStampCheckAt: number;
  isMenuLoaded: boolean;
  isLoading: boolean;
  // Menu
  loadMenu: (userId: string) => Promise<void>;
  // Icons
  loadIcons: () => Promise<void>;
  // Metadata stamp — değiştiyse nesne önbelleklerini boşaltır ve menüyü yeniler; true döner
  checkMetadataStamp: (userId: string | null, force?: boolean) => Promise<boolean>;
  // Favorites
  toggleFavorite: (userId: string, screenId: string) => Promise<void>;
  getFavorites: () => UserScreenNode[];
  // Objects
  getObject: (objectId: string) => Promise<SystemObject | null>;
  // Fields
  getFields: (objectId: string) => Promise<SystemObjectField[]>;
  // Object Details (sub-objects)
  getObjectDetails: (objectId: string) => Promise<SystemObjectDetail[]>;
  // Object Filters
  getFilters: (objectId: string) => Promise<SystemObjectFilter[]>;
  getDefaultFilterId: (objectId: string) => Promise<string | undefined>;
  // HTML Card Templates
  getHtmlCardTemplate: (templateId: string) => Promise<SystemHtmlCardTemplate | null>;
  // HTML Summary Templates
  getHtmlSummaryTemplate: (templateId: string) => Promise<SystemHtmlSummaryTemplate | null>;
  // Razor Templates
  getRazorTemplate: (templateId: string) => Promise<SystemRazorTemplate | null>;
  // Prefetch
  prefetchMetadata: (objectId: string) => Promise<void>;
  // Clear
  clearCache: () => void;
}

export const useMetadataStore = create<MetadataState>((set, get) => ({
  screens: [],
  menuTree: [],
  objects: new Map(),
  fields: new Map(),
  objectDetails: new Map(),
  objectFilters: new Map(),
  htmlCardTemplates: new Map(),
  htmlSummaryTemplates: new Map(),
  razorTemplates: new Map(),
  icons: new Map(),
  iconCodes: new Map(),
  iconsLoaded: false,
  summaryTypes: new Map(),
  metadataStamp: null,
  lastStampCheckAt: 0,
  isMenuLoaded: false,
  isLoading: false,

  loadSummaryTypes: async () => {
    if (get().summaryTypes.size > 0) return;
    try {
      const list = await systemApi.getSummaryTypes();
      const map = new Map<string, string>();
      for (const t of list || [])
        if (t.summaryTypeId) map.set(t.summaryTypeId.toLowerCase(), t.summaryTypeName);
      set({ summaryTypes: map });
    } catch {
      // özet satırı isteğe bağlı
    }
  },

  loadMenu: async (userId: string) => {
    set({ isLoading: true });
    // İkonlar menüyü bekletmez — paralel ve sessiz (web: ayrı Icons ucu, 30 dk sunucu önbelleği)
    if (!get().iconsLoaded) {
      get()
        .loadIcons()
        .catch(() => {});
    }
    try {
      // Yetkili ekranlar + üst modüller tek uçtan. Eski DAL'a karşı çalışılıyorsa
      // (uç yok) menü tamamen boş kalmasın diye yetkili listeye düşülüyor.
      const raw = (await systemApi
        .getMenuScreens(userId)
        .catch(() => systemApi.getUserScreens(userId))) as unknown as SystemScreenUserRaw[];
      const tree = buildMenuTree(raw);
      set({ menuTree: tree, isMenuLoaded: true, isLoading: false });
    } catch (e) {
      console.error('[loadMenu error]', e);
      set({ isLoading: false });
    }
  },

  loadIcons: async () => {
    // İkon listesi ~3,3 MB. Koruma olmadığı için giriş sırasında iki kez (sayfa tam
    // yenilendiğinde dörde kadar) çekiliyordu: SessionProvider ile loadMenu aynı anda
    // çağırıyor ve `iconsLoaded` ikisi de bitene kadar false kalıyordu.
    if (get().iconsLoaded) return;
    if (iconsInFlight) return iconsInFlight;
    iconsInFlight = (async () => {
      try {
        const list = await systemApi.getIcons();
        const map = new Map<string, string>();
        const codes = new Map<string, string>();
        for (const ic of list || []) {
          if (!ic?.iconId) continue;
          const key = ic.iconId.toLowerCase();
          if (ic.icon) map.set(key, ic.icon);
          if (ic.iconCode) codes.set(key, ic.iconCode);
        }
        set({ icons: map, iconCodes: codes, iconsLoaded: true });
      } catch (e) {
        console.warn('[loadIcons] ikonlar alınamadı', e);
        set({ iconsLoaded: true });
      }
    })().finally(() => {
      iconsInFlight = null;
    });
    return iconsInFlight;
  },

  // Ekleme/silme sonrası listeyi baştan çekmiyoruz: ikon listesi 3,4 MB ve ölçümde
  // yeniden indirmesi saniyeler sürüyor — eklenen ikon o süre boyunca seçicide
  // görünmüyordu. Sunucu önbelleği uçlarda temizleniyor, sonraki oturum zaten taze.
  upsertIcon: (iconId, iconCode, png) => {
    const key = iconId.toLowerCase();
    set((s) => {
      const icons = new Map(s.icons);
      const iconCodes = new Map(s.iconCodes);
      icons.set(key, png);
      iconCodes.set(key, iconCode);
      return { icons, iconCodes };
    });
  },

  removeIcon: (iconId) => {
    const key = iconId.toLowerCase();
    set((s) => {
      const icons = new Map(s.icons);
      const iconCodes = new Map(s.iconCodes);
      icons.delete(key);
      iconCodes.delete(key);
      return { icons, iconCodes };
    });
  },

  checkMetadataStamp: async (userId, force = false) => {
    const now = Date.now();
    if (!force && now - get().lastStampCheckAt < STAMP_CHECK_MIN_INTERVAL_MS) return false;
    set({ lastStampCheckAt: now });
    try {
      const res = await systemApi.getMetadataStamp();
      const stamp = res?.stamp ?? null;
      const prev = get().metadataStamp;
      if (prev === null) {
        set({ metadataStamp: stamp });
        return false;
      }
      if (stamp === prev) return false;

      // Damga değişti → nesne/alan/detay/filtre/şablon önbellekleri bayat; menüyü de tazele
      set({
        metadataStamp: stamp,
        objects: new Map(),
        fields: new Map(),
        objectDetails: new Map(),
        objectFilters: new Map(),
        htmlCardTemplates: new Map(),
        htmlSummaryTemplates: new Map(),
        razorTemplates: new Map(),
      });
      if (userId) {
        get()
          .loadMenu(userId)
          .catch(() => {});
      }
      return true;
    } catch {
      return false;
    }
  },

  toggleFavorite: async (userId: string, screenId: string) => {
    // Optimistic update — toggle in-place
    const tree = get().menuTree;

    function toggleInTree(nodes: UserScreenNode[]): boolean {
      for (const node of nodes) {
        if (node.screenId === screenId) {
          node.favorite = !node.favorite;
          return true;
        }
        if (node.children.length > 0 && toggleInTree(node.children)) return true;
      }
      return false;
    }

    toggleInTree(tree);
    set({ menuTree: [...tree] }); // Trigger re-render

    try {
      await systemApi.toggleFavorite(userId, screenId);
    } catch {
      // Rollback on error
      toggleInTree(tree);
      set({ menuTree: [...tree] });
    }
  },

  getFavorites: () => {
    const result: UserScreenNode[] = [];
    function collect(nodes: UserScreenNode[]) {
      for (const node of nodes) {
        if (node.favorite && node.objectId) result.push(node);
        if (node.children.length > 0) collect(node.children);
      }
    }
    collect(get().menuTree);
    return result;
  },

  getObject: async (objectId: string) => {
    const cached = get().objects.get(objectId);
    if (cached) return cached;
    try {
      const obj = await systemApi.getObject(objectId);
      if (obj) {
        set((state) => {
          const newMap = new Map(state.objects);
          newMap.set(objectId, obj);
          return { objects: newMap };
        });
        return obj;
      }
    } catch {
      // Ignore
    }
    return null;
  },

  getFields: async (objectId: string) => {
    const cached = get().fields.get(objectId);
    if (cached) return cached;
    try {
      const fields = await systemApi.getObjectFields(objectId);
      set((state) => {
        const newMap = new Map(state.fields);
        newMap.set(objectId, fields);
        return { fields: newMap };
      });
      return fields;
    } catch {
      return [];
    }
  },

  getObjectDetails: async (objectId: string) => {
    const cached = get().objectDetails.get(objectId);
    if (cached) return cached;
    try {
      const details = (await systemApi.getObjectDetails(objectId)) as unknown as SystemObjectDetail[];
      const sorted = (details || []).sort((a, b) => a.displayOrder - b.displayOrder);
      set((state) => {
        const newMap = new Map(state.objectDetails);
        newMap.set(objectId, sorted);
        return { objectDetails: newMap };
      });
      return sorted;
    } catch {
      return [];
    }
  },

  getFilters: async (objectId: string) => {
    const cached = get().objectFilters.get(objectId);
    if (cached) return cached;
    try {
      const filters = await systemApi.getObjectFilters(objectId);
      const sorted = (filters || []).sort((a, b) => a.displayOrder - b.displayOrder);
      set((state) => {
        const newMap = new Map(state.objectFilters);
        newMap.set(objectId, sorted);
        return { objectFilters: newMap };
      });
      return sorted;
    } catch {
      return [];
    }
  },

  getDefaultFilterId: async (objectId: string) => {
    const cached = get().objectFilters.get(objectId);
    if (cached) {
      const def = cached.find((f) => f.default) || cached[0];
      return def?.objectFilterId;
    }
    try {
      const filters = await systemApi.getObjectFilters(objectId);
      const sorted = (filters || []).sort((a, b) => a.displayOrder - b.displayOrder);
      set((state) => {
        const newMap = new Map(state.objectFilters);
        newMap.set(objectId, sorted);
        return { objectFilters: newMap };
      });
      const def = sorted.find((f) => f.default) || sorted[0];
      return def?.objectFilterId;
    } catch {
      return undefined;
    }
  },

  getHtmlCardTemplate: async (templateId: string) => {
    const cached = get().htmlCardTemplates.get(templateId);
    if (cached) return cached;
    try {
      const template = await systemApi.getHtmlCardTemplate(templateId);
      if (template) {
        set((state) => {
          const newMap = new Map(state.htmlCardTemplates);
          newMap.set(templateId, template);
          return { htmlCardTemplates: newMap };
        });
        return template;
      }
    } catch {
      // Ignore - fallback to default card
    }
    return null;
  },

  getHtmlSummaryTemplate: async (templateId: string) => {
    const cached = get().htmlSummaryTemplates.get(templateId);
    if (cached) return cached;
    try {
      const template = await systemApi.getHtmlSummaryTemplate(templateId);
      if (template) {
        set((state) => {
          const newMap = new Map(state.htmlSummaryTemplates);
          newMap.set(templateId, template);
          return { htmlSummaryTemplates: newMap };
        });
        return template;
      }
    } catch {
      // Ignore - fallback to no summary
    }
    return null;
  },

  getRazorTemplate: async (templateId: string) => {
    const cached = get().razorTemplates.get(templateId);
    if (cached) return cached;
    try {
      const template = await systemApi.getRazorTemplate(templateId);
      if (template) {
        set((state) => {
          const newMap = new Map(state.razorTemplates);
          newMap.set(templateId, template);
          return { razorTemplates: newMap };
        });
        return template;
      }
    } catch {
      // Ignore - fallback to default grid
    }
    return null;
  },

  prefetchMetadata: async (objectId: string) => {
    await Promise.all([get().getObject(objectId), get().getFields(objectId)]);
  },

  clearCache: () => {
    set({
      screens: [],
      menuTree: [],
      objects: new Map(),
      fields: new Map(),
      objectDetails: new Map(),
      objectFilters: new Map(),
      htmlCardTemplates: new Map(),
      htmlSummaryTemplates: new Map(),
      razorTemplates: new Map(),
      icons: new Map(),
      iconCodes: new Map(),
      iconsLoaded: false,
      metadataStamp: null,
      lastStampCheckAt: 0,
      isMenuLoaded: false,
    });
  },
}));
